/* eslint new-cap: "off"*/
import * as R from 'ramda';
import Backbone from 'backbone';
import Type from 'union-type';

import { fetchCompanies } from 'api/users';

import { EUR, DKK } from 'modules/config/currencies';
import { post } from 'modules/api';
import { redirectTo } from 'modules/history';
import { convertLocaleToAPI } from 'modules/utils/locales.js';
import { userSelector } from 'modules/currentUser';

import { fetchPaymentStatus as paymentFetchPaymentStatus } from 'models/payment';
import { createGiftCardPaymentAnonymous } from 'api/boutique';
import { LOGOUT, createApiActionTypes, createRemoteType } from '../../actions';
import reducerRegistry from '../../reducers/ReducerRegistry';

function buildRedirectUrl(url) {
    return `${location.protocol}//${
        location.port ? `${location.hostname}:${location.port}` : location.hostname
    }${Backbone.history.root}${url}`;
}

export const INVOICE = 'invoice';
export const KYC = 'kyc';
export const UNKNOWN_ERROR = 'error';
export const DO_NOT_PROCESS_THIS_ERROR = 'THIS HAS TO STOP';
export const GLOBAL_STATE_KEY = 'giftCard';
export const LOGIN = 'login';

const ac = strs => `${GLOBAL_STATE_KEY}/${strs[0]}`;

export const actionTypes = {
    SET_CURRENCY: ac`SET_CURRENCY`,
    SET_ANONYMOUS: ac`SET_ANONYMOUS`,
    SET_INVOICE_REQUESTABLE: ac`SET_INVOICE_REQUESTABLE`,
    EDIT_GIFTCARD_FORM: ac`EDIT_GIFTCARD_FORM`,
    EDIT_INVOICE_FORM: ac`EDIT_INVOICE_FORM`,
    EDIT_ANONYMOUS_FORM: ac`EDIT_ANONYMOUS_FORM`,
    LOCK_FORMS: ac`LOCK_FORMS`,
    UNLOCK_FORMS: ac`UNLOCK_FORMS`,
    PAYMENT_PROCESS_ERRORS: ac`PAYMENT_PROCESS_ERRORS`,
    CREATE_PAYMENT: ac`CREATE_PAYMENT`,
    RESET: ac`RESET`,
    FETCH_PAYMENT_STATUS: createApiActionTypes(GLOBAL_STATE_KEY, 'FETCH_PAYMENT_STATUS'),
    FETCH_COMPANY: createApiActionTypes(GLOBAL_STATE_KEY, 'FETCH_COMPANY'),
    REQUEST_INVOICE: ac`REQUEST_INVOICE`,
    SHOW_INVOICE_FORM: ac`SHOW_INVOICE_FORM`,
    SHOW_LOGIN: ac`SHOW_LOGIN`,
};

const CompanyStatus = createRemoteType(Object, Error);

const PaymentStatus = createRemoteType(Object, Error);

const initialState = {
    canRequestInvoice: false,
    currency: EUR,
    giftCardForm: {
        value: '20,00',
        amount: 2000, // intermediate value for big currencies
        quantity: 1,
    },
    invoiceForm: {
        _country: '',
        _zip: '',
        cityId: '',
        companyName: '',
        companyNumber: '',
        street: '',
        vatNumber: '',
    },
    anonymousForm: {
        firstName: '',
        lastName: '',
        email: '',
    },
    invoiceFormReady: false,
    invoiceRequested: false,
    isPaymentPageBeingCreated: false,
    cardId: null,
    paymentUuid: null,
    locked: false, // difference between form and confirm ...
    state: '',
    companyStatus: CompanyStatus.Init,
    paymentStatus: PaymentStatus.Init,
    isAnonymous: false,
};

function formEdit(key, payload) {
    return R.evolve({
        [key]: R.merge(R.__, {
            [payload.key]: payload.value,
        }),
    });
}

const setDefaultValuesForInvoiceForm = (state, company, store) => {
    if (!company) {
        const user = userSelector(store);

        return R.mergeDeepRight(state, {
            invoiceForm: {
                _country: (R.path(['address', 'country', 'id'], user) || '').toString(),
                _zip: R.path(['address', 'city', 'zipCode'], user) || '',
                cityId: R.path(['address', 'city', 'id'], user) || '',
                companyName: `${user.firstName} ${user.lastName}`,
                street: R.path(['address', 'street'], user) || '',
            },
            invoiceFormReady: true,
        });
    }

    return R.mergeDeepRight(state, {
        invoiceForm: {
            _country: (R.path(['address', 'country', 'id'], company) || '').toString(),
            _zip: R.path(['address', 'city', 'zipCode'], company) || '',
            cityId: R.path(['address', 'city', 'id'], company) || '',
            companyName: R.prop('name', company) || '',
            companyNumber: R.prop('companyNumber', company) || '',
            street: R.path(['address', 'street'], company) || '',
            vatNumber: R.prop('vatNumber', company) || '',
        },
        invoiceFormReady: true,
    });
};

export const giftCardReducer = (state = initialState, { type, payload, store }) => {
    switch (type) {
        case LOGOUT:
        case actionTypes.RESET:
            return initialState;
        case actionTypes.SET_CURRENCY:
            return R.mergeDeepRight(state, {
                currency: payload,
                giftCardForm: {
                    amount: payload === DKK ? 30000 : 2000,
                },
            });
        case actionTypes.SET_INVOICE_REQUESTABLE:
            return R.merge(state, {
                canRequestInvoice: payload,
                invoiceRequested: payload,
            });
        case actionTypes.EDIT_GIFTCARD_FORM:
            return formEdit('giftCardForm', payload)(state);
        case actionTypes.EDIT_INVOICE_FORM:
            return formEdit('invoiceForm', payload)(state);
        case actionTypes.EDIT_ANONYMOUS_FORM:
            return formEdit('anonymousForm', payload)(state);
        case actionTypes.LOCK_FORMS:
            return R.merge(state, {
                locked: true,
            });
        case actionTypes.UNLOCK_FORMS:
            return R.merge(state, {
                state: '',
                locked: false,
            });
        case actionTypes.CREATE_PAYMENT:
            return R.merge(state, { paymentUuid: payload });
        case actionTypes.PAYMENT_PROCESS_ERRORS:
            return R.merge(state, {
                state: payload,
            });
        case actionTypes.FETCH_COMPANY.REQUEST:
            return R.merge(state, {
                invoiceFormReady: false,
            });
        case actionTypes.FETCH_COMPANY.SUCCESS:
            const company = payload;
            return setDefaultValuesForInvoiceForm(state, company, store);
        case actionTypes.FETCH_COMPANY.FAILURE:
            return setDefaultValuesForInvoiceForm(state, null, store);
        case actionTypes.FETCH_PAYMENT_STATUS.REQUEST:
            return R.merge(state, {
                paymentStatus: PaymentStatus.Loading,
            });
        case actionTypes.FETCH_PAYMENT_STATUS.SUCCESS:
            return R.merge(state, {
                paymentStatus: PaymentStatus.Loaded(payload),
                state: 'success',
            });
        case actionTypes.FETCH_PAYMENT_STATUS.FAILURE:
            return R.merge(state, {
                paymentStatus: PaymentStatus.Error(payload),
                state: UNKNOWN_ERROR,
            });
        case actionTypes.REQUEST_INVOICE:
            return R.merge(state, {
                invoiceRequested: payload,
            });
        case actionTypes.SHOW_INVOICE_FORM:
            return R.merge(state, {
                state: INVOICE,
            });
        case actionTypes.SHOW_LOGIN:
            return R.merge(state, {
                state: LOGIN,
            });
        case actionTypes.SET_ANONYMOUS:
            return R.merge(state, {
                isAnonymous: payload,
                invoiceFormReady: true, // the invoice form will be empty
            });
        default:
            return state;
    }
};

reducerRegistry.register({
    [GLOBAL_STATE_KEY]: giftCardReducer,
});

const createFormEditActionCreator = type => (name, value) => ({
    type,
    payload: {
        key: name,
        value,
    },
});

export const editGiftCardForm = createFormEditActionCreator(actionTypes.EDIT_GIFTCARD_FORM);

export const editInvoiceForm = createFormEditActionCreator(actionTypes.EDIT_INVOICE_FORM);

export const editAnonymousForm = createFormEditActionCreator(actionTypes.EDIT_ANONYMOUS_FORM);

export const setCurrency = currency => ({
    type: actionTypes.SET_CURRENCY,
    payload: currency,
});

export const setInvoiceRequestable = bool => ({
    type: actionTypes.SET_INVOICE_REQUESTABLE,
    payload: bool,
});

export const unlockForms = () => dispatch => {
    return dispatch({
        type: actionTypes.UNLOCK_FORMS,
    });
};

export const fetchCompany = userId => (dispatch, getState) => {
    dispatch({
        type: actionTypes.FETCH_COMPANY.REQUEST,
    });

    return fetchCompanies(userId).then(
        res =>
            dispatch({
                type: actionTypes.FETCH_COMPANY.SUCCESS,
                payload:
                    res &&
                    res.companies &&
                    R.find(R.propEq('status', 'company.validated'), res.companies),
                store: getState(),
            }),
        err =>
            dispatch({
                type: actionTypes.FETCH_COMPANY.FAILURE,
                payload: err,
                store: getState(),
            })
    );
};

export const fetchPaymentStatus = paymentUuid => dispatch => {
    dispatch({
        type: actionTypes.FETCH_PAYMENT_STATUS.REQUEST,
    });
    return paymentFetchPaymentStatus(paymentUuid).then(
        res =>
            dispatch({
                type: actionTypes.FETCH_PAYMENT_STATUS.SUCCESS,
                payload: res,
            }),
        err =>
            dispatch({
                type: actionTypes.FETCH_PAYMENT_STATUS.FAILURE,
                payload: err,
            })
    );
};

const createPayment = () => (dispatch, getState) => {
    const state = getState()[GLOBAL_STATE_KEY];

    const redirectUrl = buildRedirectUrl('giftcards/return/{uuid}');

    const params = {
        amount: state.giftCardForm.amount,
        appReturnUrl: redirectUrl,
        currency: state.currency,
        invoiceData: state.invoiceRequested ? state.invoiceForm : null,
        quantity: state.giftCardForm.quantity,
        redirectUrl,
        redirectUrlCancel: redirectUrl,
        redirectUrlError: redirectUrl,
    };

    if (state.isAnonymous) {
        params.buyer = state.anonymousForm;
        params.buyer.locale = convertLocaleToAPI(getState().locale);

        return createGiftCardPaymentAnonymous(params).then(
            payment => {
                dispatch({
                    type: actionTypes.CREATE_PAYMENT,
                    payload: payment.uuid,
                });

                return payment;
            },
            () => {
                dispatch({
                    type: actionTypes.PAYMENT_PROCESS_ERRORS,
                    payload: UNKNOWN_ERROR,
                });

                throw DO_NOT_PROCESS_THIS_ERROR;
            }
        );
    }

    return post('boutique/gift_cards/buy', params).then(
        payment => {
            dispatch({
                type: actionTypes.CREATE_PAYMENT,
                payload: payment.uuid,
            });

            return payment;
        },
        err => {
            const isMissingUserInformation =
                err.status === 400 &&
                err.responseJSON &&
                err.responseJSON.error === 'MissingUserInformation';

            dispatch({
                type: actionTypes.PAYMENT_PROCESS_ERRORS,
                payload: isMissingUserInformation ? KYC : UNKNOWN_ERROR,
            });

            throw DO_NOT_PROCESS_THIS_ERROR;
        }
    );
};

export const setIsAnonymous = isAnonymous => dispatch => {
    dispatch({
        type: actionTypes.SET_ANONYMOUS,
        payload: isAnonymous,
    });
};

export const continueToInvoiceForm = () => dispatch => {
    dispatch({
        type: actionTypes.SHOW_INVOICE_FORM,
    });
};

export const redirectToPaymentPage = () => dispatch => {
    dispatch({
        type: actionTypes.LOCK_FORMS,
    });
    return dispatch(createPayment()).then(({ paymentUrl }) => redirectTo(paymentUrl));
};

export const requestInvoice = isRequested => dispatch => {
    dispatch({
        type: actionTypes.REQUEST_INVOICE,
        payload: isRequested,
    });
};

export const continueToLogin = () => dispatch => {
    dispatch({
        type: actionTypes.SHOW_LOGIN,
    });
};

export const selectLockedForm = state => state[GLOBAL_STATE_KEY].locked;

export const selectGiftCardForm = state => state[GLOBAL_STATE_KEY].giftCardForm;

export const selectInvoiceForm = state => state[GLOBAL_STATE_KEY].invoiceForm;
export const selectInvoiceFormReady = state => state[GLOBAL_STATE_KEY].invoiceFormReady;
export const selectInvoiceRequestable = state => state[GLOBAL_STATE_KEY].canRequestInvoice;
export const selectInvoiceRequested = state => state[GLOBAL_STATE_KEY].invoiceRequested;

export const selectCurrency = state => state[GLOBAL_STATE_KEY].currency;

export const selectAnonymousForm = state => state[GLOBAL_STATE_KEY].anonymousForm;

export const selectIsAnonymous = state => state[GLOBAL_STATE_KEY].isAnonymous;

const View = Type({
    Loading: [],
    Form: [],
    Invoice: [],
    Success: [],
    Error: [],
    Kyc: [],
    Login: [],
});

export const selectView = _state => {
    const state = _state[GLOBAL_STATE_KEY];

    const verifyingPaymentStatus = state.paymentStatus.case({
        Loading: () => true,
        _: () => false,
    });

    switch (true) {
        case verifyingPaymentStatus:
            return View.Loading;
        case state.state === UNKNOWN_ERROR:
            return View.Error;
        case state.state === INVOICE:
            return View.Invoice;
        case state.state === KYC:
            return View.Kyc;
        case state.state === 'success':
            return View.Success;
        case state.state === LOGIN:
            return View.Login;
        default:
            return View.Form;
    }
};
